import { useState, useEffect, useRef, ChangeEvent } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { DefaultSelect } from 'pages/calculator/MediaPlanning/common/DefaultSelect/DefaultSelect';
import WeeklyDatePicker from 'pages/calculator/MediaPlanning/common/WeeklyDatePicker/WeeklyDatePicker';
import usePlanStore, { CountryFormState } from 'store/planStore';
import useTargetGroups from 'pages/calculator/MediaPlanning/Plan/Countries/hooks/useTargetGroups';
import useCampaignScenario from 'pages/calculator/MediaPlanning/Plan/Countries/hooks/useCampaignScenario';
import useSegments from 'pages/calculator/MediaPlanning/Plan/Countries/hooks/useSegments';
import CountrySetupButtonGroup from 'pages/calculator/MediaPlanning/Plan/Countries/CountryPanel/CountrySetupButtonGroup/CountrySetupButtonGroup';
import useCampaignLength from '../hooks/useCampaignLength';
import RecalculateTouchpointsDialog from './RecalculateTouchpointsDialog/RecalculateTouchpointsDialog';
import MultiSelectAutocomplete from '../../../common/MultiSelectAutocomplete/MultiSelectAutocomplete';
import HelperTooltip from '../../../../../../shared/HelperTooltip/HelperTooltip';
import linksConfig from '../../../../../../config/linking';
import useLiveTouchpoints from '../hooks/useLiveTouchpoints';
import { Touchpoints } from '../../enums';

const TV_ITEMS = [
  { value: 'included', name: 'TV in plan' },
  { value: 'not_included', name: 'TV not in plan' },
];

interface CountryPanelProps {
  country: string;
  setIsClearFormDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function CountryPanel({
  country,
  setIsClearFormDialogOpen,
}: CountryPanelProps) {
  const [
    isOpenRecalculateTouchpointsModal,
    setIsOpenRecalculateTouchpointsModal,
  ] = useState(false);
  const { t } = useTranslation();
  const {
    countryForms,
    savedCountryForms,
    selectedCountry,
    validationErrors,
    updateCountryForm,
    validateCountryForm,
    resetCountryFormConfig,
    saveCountryFormState,
    resetCalculatedBudget,
    resetSavedCalculatedBudget,
    resetStoredCountryForm,
    setActiveNestedTab,
    resetTVConfig,
    resetOOHConfig,
    setCountryOneReach,
    updateTVAvailable,
  } = usePlanStore();

  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const countryForm = countryForms[selectedCountry!];

  const formData = countryForms[country] || {
    targetGroup: '',
    campaignScenario: '',
    segments: [],
    startDate: '',
    campaignLength: '',
    isTVIncluded: null,
    isTVMandatory: false,
  };

  const savedData = savedCountryForms[country] || {
    targetGroup: '',
    campaignScenario: '',
    segment: '',
    startDate: '',
    campaignLength: '',
    isTVIncluded: null,
    isTVMandatory: false,
  };

  const { data: targetGroups, isFetching: isFetchingTargetGroups } =
    useTargetGroups(country);

  const { data: campaignScenarios, isFetching: isFetchingCampaignScenarios } =
    useCampaignScenario(country);

  const { data: segments, isFetching: isFetchingSegments } =
    useSegments(country);

  const { data: campaignLengths, isFetching: isFetchingCampaignLengths } =
    useCampaignLength(country);

  const {
    data: availableTouchpoints,
    isFetching: isFetchingAvailableTouchpoints,
  } = useLiveTouchpoints();

  const tvTouchpoint = availableTouchpoints.find(
    (touchpoint) => touchpoint.TouchpointName === Touchpoints.tv
  );

  const isTVMandatory = !!tvTouchpoint?.isMustHave;

  const prevTouchpointFieldsRef = useRef({
    country,
    targetGroup: formData.targetGroup,
    campaignScenario: formData.campaignScenario,
  });

  useEffect(() => {
    const prev = prevTouchpointFieldsRef.current;
    const sameCountry = prev.country === country;
    const targetGroupChanged = prev.targetGroup !== formData.targetGroup;
    const campaignScenarioChanged =
      prev.campaignScenario !== formData.campaignScenario;

    prevTouchpointFieldsRef.current = {
      country,
      targetGroup: formData.targetGroup,
      campaignScenario: formData.campaignScenario,
    };

    if (sameCountry && (targetGroupChanged || campaignScenarioChanged)) {
      updateCountryForm(country, { isTVIncluded: null });
    }
  }, [
    country,
    formData.targetGroup,
    formData.campaignScenario,
    updateCountryForm,
  ]);

  useEffect(() => {
    if (isTVMandatory) {
      updateCountryForm(country, { isTVIncluded: true, isTVMandatory: true });
      updateTVAvailable(country, true);
    }
  }, [isTVMandatory, country, updateCountryForm, updateTVAvailable]);

  const getTvSelectValue = (): string | null => {
    if (isTVMandatory || formData.isTVIncluded === true) return 'included';
    if (formData.isTVIncluded === false) return 'not_included';
    return null;
  };
  const tvSelectValue = getTvSelectValue();

  const handleTVChange = (value: string) => {
    const isIncluded = value === 'included';
    updateCountryForm(country, { isTVIncluded: isIncluded });
  };

  const handleInputChange = async (
    field: string,
    value: string | number | undefined | { id: string; title: string }
  ) => {
    const updatedData = {
      ...formData,
      [field]: value || '',
    } as CountryFormState;

    updateCountryForm(country, updatedData);
    validateCountryForm(country, updatedData);
  };

  const handleChangeStateValues = (
    key: string,
    value: { id: string; title: string }[]
  ) => {
    updateCountryForm(country, { [key]: value } as Partial<CountryFormState>);
  };

  const handleRecalculateTouchpoints = () => {
    const currentIsTVIncluded = formData.isTVIncluded;
    resetCountryFormConfig(country);
    resetCalculatedBudget(country);
    resetStoredCountryForm(country);
    resetSavedCalculatedBudget(country);
    setCountryOneReach(country, undefined);
    resetTVConfig(country);
    resetOOHConfig(country);
    saveCountryFormState(country);
    setActiveNestedTab('digital');
    setIsOpenRecalculateTouchpointsModal(false);
    if (currentIsTVIncluded === true) {
      updateTVAvailable(country, true);
    }
  };

  useEffect(() => {
    const errors = validationErrors[country] || {};
    setLocalErrors(errors);
  }, [country, validationErrors]);

  const isTVSelectionRequired =
    !!tvTouchpoint && !isTVMandatory && !isFetchingAvailableTouchpoints;
  const isTVSelectionMissing =
    isTVSelectionRequired && formData.isTVIncluded === null;

  const isProceedDisabled =
    !!Object.keys(localErrors).length ||
    !formData.targetGroup ||
    !formData.campaignLength ||
    !formData.startDate ||
    !formData.campaignScenario ||
    !formData.segments ||
    isTVSelectionMissing ||
    (formData.targetGroup === savedData.targetGroup &&
      formData.campaignLength === savedData.campaignLength &&
      formData.startDate === savedData.startDate &&
      formData.campaignScenario === savedData.campaignScenario &&
      formData.segments === savedData.segments &&
      formData.isTVIncluded === savedData.isTVIncluded);

  const handleCLoseRecalculateTouchpointsModal = () => {
    setIsOpenRecalculateTouchpointsModal(false);
  };

  const isMetaReserved =
    countryForm.metaConfig?.reservationStatus === 'reserved' ||
    countryForm.metaConfig?.reservationStatus === 'partially-reserved';

  const singleCampaignLength =
    campaignLengths.length === 1 ? campaignLengths[0].value : undefined;
  const hasSingleCampaignLength = singleCampaignLength !== undefined;

  useEffect(() => {
    if (isFetchingCampaignLengths || !hasSingleCampaignLength || isMetaReserved)
      return;

    if (String(formData.campaignLength) === String(singleCampaignLength))
      return;

    handleInputChange('campaignLength', singleCampaignLength);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    country,
    singleCampaignLength,
    hasSingleCampaignLength,
    isFetchingCampaignLengths,
    formData.campaignLength,
    isMetaReserved,
  ]);

  return (
    <>
      <Accordion
        disableGutters
        defaultExpanded
        sx={{
          mb: '16px',
          '& .MuiAccordionSummary-root': {
            backgroundColor: 'common.white',
            alignItems: 'center',
            height: '48px',
            borderBottom: '1px solid #C7D8F9',
            borderTop: '1px solid #C7D8F9',
            pl: 0,
            '& .MuiAccordionSummary-content': {
              textAlign: 'center',
              height: 'initial',
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              mr: 0,
            },
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography
            variant="h6"
            color="#525F81"
            fontWeight={700}
            fontSize="16px"
            lineHeight="3"
            textTransform="uppercase"
          >
            {t('mediaPlanning.countrySetup')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ mt: '46px', pb: '32px' }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', rowGap: '24px' }}
          >
            <Grid container>
              <Grid item xs={3} sx={{ pr: '24px' }}>
                <DefaultSelect
                  value={formData.targetGroup}
                  onChange={(newValue) =>
                    handleInputChange('targetGroup', newValue.value)
                  }
                  items={targetGroups || []}
                  selectName={t('mediaPlanning.targetGroup')}
                  labelId="target-group"
                  disabled={isFetchingTargetGroups || isMetaReserved}
                  isLoading={isFetchingTargetGroups}
                />
              </Grid>
              <Grid
                item
                xs={3}
                sx={{ pr: '24px', display: 'flex', alignItems: 'flex-start' }}
              >
                <DefaultSelect
                  value={formData.campaignScenario}
                  onChange={(newValue) =>
                    handleInputChange('campaignScenario', newValue.value)
                  }
                  items={campaignScenarios || []}
                  selectName={t('mediaPlanning.campaignScenario')}
                  labelId="campaign-scenario"
                  disabled={
                    isFetchingCampaignScenarios ||
                    !countryForms[country]?.targetGroup ||
                    isMetaReserved
                  }
                  isLoading={isFetchingCampaignScenarios}
                />
                <HelperTooltip
                  tooltipData={linksConfig.fullMediaScenariosDefinition}
                />
              </Grid>
              <Grid
                item
                xs={3}
                sx={{ pr: '24px', display: 'flex', alignItems: 'flex-start' }}
              >
                <MultiSelectAutocomplete
                  options={segments.map((segment) => ({
                    id: segment?.value,
                    title: segment?.name,
                  }))}
                  id="partnersMultiSelect"
                  label={t('mediaPlanning.segment')}
                  noOptionsText={t('mediaPlanning.noPartners')}
                  onChange={(
                    _: ChangeEvent,
                    newValue: { id: string; title: string }[]
                  ) => {
                    handleChangeStateValues('segments', newValue);
                  }}
                  value={formData.segments || []}
                  limitTags={1}
                  disableClearable
                  styles={{ width: '100%' }}
                  disabled={
                    isFetchingSegments ||
                    !countryForms[country]?.campaignScenario ||
                    isMetaReserved
                  }
                  isLoading={isFetchingSegments}
                />
                <HelperTooltip
                  tooltipData={linksConfig.sociovisionSegmentsDefinition}
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={3} sx={{ pr: '24px' }}>
                <WeeklyDatePicker
                  label={t('mediaPlanning.startDate')}
                  value={formData.startDate || undefined}
                  setState={(value) => handleInputChange('startDate', value)}
                  disabled={isMetaReserved}
                />
              </Grid>
              <Grid item xs={3} sx={{ pr: '24px' }}>
                <DefaultSelect
                  value={formData.campaignLength}
                  onChange={(newValue) =>
                    handleInputChange('campaignLength', newValue.value)
                  }
                  items={campaignLengths || []}
                  selectName={t('mediaPlanning.campaignLength')}
                  labelId="campaignLength"
                  disabled={
                    isFetchingCampaignLengths ||
                    !countryForms[country]?.campaignScenario ||
                    isMetaReserved ||
                    hasSingleCampaignLength
                  }
                  isLoading={isFetchingCampaignLengths}
                />
              </Grid>
              <Grid item xs={3} sx={{ pr: '24px' }}>
                <DefaultSelect
                  value={tvSelectValue}
                  onChange={(newValue) =>
                    handleTVChange(newValue.value as string)
                  }
                  items={TV_ITEMS}
                  selectName="TV presence"
                  labelId="tv-inclusion"
                  disabled={!tvTouchpoint || isTVMandatory}
                  isLoading={isFetchingAvailableTouchpoints}
                  error={isTVSelectionMissing}
                />
                {isTVMandatory && (
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 400,
                      color: 'textLight.main',
                      mt: '4px',
                    }}
                  >
                    Mandatory
                  </Typography>
                )}
                {isTVSelectionMissing && (
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 400,
                      color: 'error.main',
                      mt: '4px',
                    }}
                  >
                    Select an option
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Grid container>
              <CountrySetupButtonGroup
                country={country}
                toggleResetDialog={setIsClearFormDialogOpen}
                isProceedDisabled={isProceedDisabled}
                setIsOpenRecalculateTouchpointsModal={
                  setIsOpenRecalculateTouchpointsModal
                }
              />
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>
      <RecalculateTouchpointsDialog
        open={isOpenRecalculateTouchpointsModal}
        onClose={handleCLoseRecalculateTouchpointsModal}
        onSuccess={handleRecalculateTouchpoints}
      />
    </>
  );
}
